import React, { useContext } from 'react'

import { ArticleBlockContent } from 'component-library/publication/ArticleBlockContent/ArticleBlockContent'
import { Footnote } from 'component-library/publication/Footnote/Footnote'

import { TopicList } from 'component-library/content/TopicList/TopicList'

import { getCanonicalLink } from 'component-library/templates/Publication/lib/get-canonical-link'
import { PublicationProps } from 'component-library/templates/Publication/Publication.props'
import { useFeature } from 'common/feature-togglings/features'
import { RelatedContent } from 'component-library/content/RelatedContent/RelatedContent'
import { LinkBanner } from 'component-library/banners/LinkBanner/LinkBanner'
import {
    StyledGrid,
    StyledCell,
    StyledArticleContent,
    StyledFlexCell,
    StyledInsetCell,
    StyledArticleContentFooter,
} from './TheWestFeature.styled'
import { Byline } from 'component-library/publication/Byline/Byline'
import { ConfigurationContext } from 'common/context-providers/configuration-context'
import { useSectionMeta } from 'common/routing/use-section-meta'
import { getBaseUrl } from 'common/routing/url'

interface CellProps {
    offset?: {
        position: 'top'
        value: '50%'
    }
}

export type Props = PublicationProps & CellProps

export function TheWestFeature(props: Props) {
    const publication = props.data
    const content = publication.content
    const canonicalLink = getCanonicalLink(publication)
    const { publicUrl } = useContext(ConfigurationContext)
    const sectionMeta = useSectionMeta()
    const articleUrl = `${getBaseUrl(publicUrl, sectionMeta)}/${publication.primaryTopic.id}/${
        publication.slug
    }`

    // isPremium is only avaliable on the ArticlePublication interface.
    const isPremium = publication.kind === 'article' ? publication.isPremium : false

    const relatedItems = props.relatedPublications && props.relatedPublications.items
    const userTopicItems = props.userTopicPublications && props.userTopicPublications.items

    const enableMyHome = useFeature('my-home')

    const renderUserTopicPublications = enableMyHome && userTopicItems && (
        <RelatedContent title="My Topics" items={userTopicItems} onEvent={props.onEvent} />
    )

    return (
        <StyledGrid itemProp="articleBody">
            <StyledInsetCell>
                <Byline
                    profile={publication.profile}
                    text={publication.byline}
                    isSponsored={publication.isSponsored}
                    timestamp={publication.publicationDate}
                    source={publication.source}
                    isLive={publication.isLive}
                    isFullWidth={true}
                    url={articleUrl}
                    title={publication.heading}
                    isPremium={isPremium}
                />
            </StyledInsetCell>

            <StyledCell>
                {content && (
                    <StyledArticleContent>
                        <ArticleBlockContent
                            blocks={content}
                            targetedContentElement={
                                props.targetedContent && props.targetedContent.position === 'inline'
                                    ? props.targetedContent.element
                                    : undefined
                            }
                            inlinePositionedContent={props.inlinePositionedContent}
                            onEvent={props.onEvent}
                            log={props.log}
                            publicationPathname={props.location.pathname}
                            adUnitPath={props.adUnitPath}
                        />
                        {canonicalLink && (
                            <Footnote text="Originally published as " link={canonicalLink} />
                        )}
                    </StyledArticleContent>
                )}

                <StyledArticleContentFooter>
                    <LinkBanner
                        url={`https://emails.thewest.com.au/subscribe/index.html?utm_source=${sectionMeta.Hostname ||
                            'TheWest'}&utm_campaign=ArticleLinkBanner&utm_content=ArticleLinkBanner`}
                        message="Get the latest news delivered to your inbox."
                        cta="Sign up for our emails"
                        verticalGutters={['md', 'md']}
                    />

                    {relatedItems && (
                        <StyledFlexCell>
                            {renderUserTopicPublications}
                            <RelatedContent items={relatedItems} onEvent={props.onEvent} />
                        </StyledFlexCell>
                    )}
                    <StyledCell>
                        <TopicList
                            primaryTopic={publication.primaryTopic}
                            secondaryTopics={publication.secondaryTopics}
                        />
                    </StyledCell>
                </StyledArticleContentFooter>
            </StyledCell>
        </StyledGrid>
    )
}
